import { TechDataVehicle } from '@/types';
import { pushDataLayerEvent } from '@/utils/tracking';
import { getEnvData } from './../utils/aem';
import type { PageEnvironmentVariables } from '@seamless/one-context';
/**
 * Composable for tracking various user interactions in the Tech Data component.
 * @param modelSeries - The model series code for the vehicle.
 */
export function useTracking(modelSeries: string) {
  let envData: PageEnvironmentVariables | null = null;

  async function fetchEnvData() {
    envData = (await getEnvData()).pageEnvironmentVariables;
  }

  fetchEnvData();
  /**
   * Helper to build the vehicle tracking object.
   */
  const buildVehicleObj = (vehicle: TechDataVehicle) => ({
    modelCode: modelSeries,
    class: vehicle.vehicleClass,
    bodyType: vehicle.bodyType,
    baumusterId: vehicle.baumuster,
    type: 'van',
    category: 'commercial',
  });

  /**
   * Helper to send tracking events and log in non-production environments.
   */
  const trackEvent = (
    name: string,
    category: string,
    action: string,
    label: string,
    vehicle: TechDataVehicle | null,
  ) => {
    if (!vehicle) {
      console.warn('No vehicle selected for tracking.');
      return;
    }
    const eventData = {
      name,
      category,
      action,
      label,
      vehicle: buildVehicleObj(vehicle),
    };

    if (!envData || envData.stage !== 'PROD') {
      // eslint-disable-next-line no-console
      // This log is for testing before deployment to production
      console.log('eventData', eventData);
    }
    pushDataLayerEvent(eventData);
  };

  /**
   * Track feature selection event.
   */
  const trackFeatureSelection = (
    label: string,
    vehicle: TechDataVehicle | null,
  ) => {
    trackEvent(
      'feature',
      'feature',
      'cvans_techdata_component',
      label.toLowerCase(),
      vehicle,
    );
  };

  /**
   * Track slider interaction event.
   */
  const trackSlider = (label: string, vehicle: TechDataVehicle | null) => {
    trackEvent(
      'feature',
      'feature',
      'cvans_techdata_component',
      label,
      vehicle,
    );
  };

  /**
   * Track accordion interaction event.
   */
  const trackAccordion = (label: string, vehicle: TechDataVehicle | null) => {
    trackEvent(
      'show_more_details',
      'feature',
      'cvans_techdata_component',
      label,
      vehicle,
    );
  };

  /**
   * Track impression event.
   */
  const trackImpression = (vehicle: TechDataVehicle | null) => {
    trackEvent(
      'impression',
      'impression',
      'cvans_techdata_component',
      'cvans_techdata',
      vehicle,
    );
  };

  /**
   * Track modal button click event.
   */
  const trackModalButton = (label: string, vehicle: TechDataVehicle | null) => {
    trackEvent(
      'open_modal',
      'feature',
      'cvans_techdata_component',
      label.toLowerCase(),
      vehicle,
    );
  };

  /**
   * Track modal scroll event.
   */
  const trackModalScroll = (label: string, vehicle: TechDataVehicle | null) => {
    trackEvent(
      'impression',
      'impression',
      'cvans_techdata_component',
      label.toLowerCase(),
      vehicle,
    );
  };

  return {
    trackFeatureSelection,
    trackSlider,
    trackAccordion,
    trackImpression,
    trackModalButton,
    trackModalScroll,
  };
}
